import React from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import type { Valuation } from '../../contexts/CatalogContext';
import { formatValuationDisplay, stripValuationFormatting } from '../../utils/catalogFormatters';

const CURRENCY_CODES = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'INR'];

interface ValuationRowsProps {
  valuations: Valuation[];
  onChange: (next: Valuation[]) => void;
  disabled?: boolean;
}

const blankRow: Valuation = { value: '', currency: 'USD', type: '' };

/**
 * Editable list of value/currency/type rows. Always renders at least one row
 * so the user can start typing immediately; the trailing "+ Add another
 * valuation" button appends a fresh row. Per-row delete sends an updated
 * array up; deleting the only row collapses back to a single blank row.
 */
export const ValuationRows: React.FC<ValuationRowsProps> = ({
  valuations,
  onChange,
  disabled,
}) => {
  const rows = valuations.length === 0 ? [blankRow] : valuations;

  const update = (index: number, patch: Partial<Valuation>) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const remove = (index: number) => {
    const next = rows.filter((_, i) => i !== index);
    // Collapse to a single empty row rather than emit []; matches the visual
    // of the Add another valuation button always staying visible.
    onChange(next.length === 0 ? [] : next);
  };

  const add = () => {
    onChange([...rows, blankRow]);
  };

  return (
    <Box>
      {rows.map((row, index) => (
        <Box
          key={index}
          data-testid="valuation-row"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr auto' },
            gap: 1.5,
            mb: 1.5,
            alignItems: 'center',
          }}
        >
          <TextField
            select
            label="Currency"
            value={row.currency}
            onChange={(e) => update(index, { currency: e.target.value })}
            disabled={disabled}
            fullWidth
            inputProps={{ 'aria-label': `Valuation ${index + 1} currency` }}
          >
            {CURRENCY_CODES.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Value"
            placeholder="0.00"
            value={formatValuationDisplay(row.value)}
            onChange={(e) => update(index, { value: stripValuationFormatting(e.target.value) })}
            disabled={disabled}
            fullWidth
            inputProps={{ 'aria-label': `Valuation ${index + 1} value` }}
          />
          <TextField
            label="Type"
            placeholder="e.g. Insurance, custom…"
            value={row.type}
            onChange={(e) => update(index, { type: e.target.value })}
            disabled={disabled}
            fullWidth
            inputProps={{ 'aria-label': `Valuation ${index + 1} type` }}
          />
          <IconButton
            onClick={() => remove(index)}
            size="small"
            disabled={disabled}
            aria-label={`Remove valuation ${index + 1}`}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button
        startIcon={<Add />}
        onClick={add}
        variant="outlined"
        size="small"
        disabled={disabled}
        sx={{ borderStyle: 'dashed', width: '100%' }}
      >
        Add another valuation
      </Button>
    </Box>
  );
};
